import {
  settingSVG,
  pspSvg,
  designSVG,
  shipSVG,
  notificationSVG,
  marketingSVG,
  classesSVG,
  dashboardSVG,
  discountsSVG,
  securitySVG,
  subscribeSVG,
  webhookSVG,
  RecommendationsSVG,
  userSettingSVG,
  rocketSVG,
  eventSVG,
  keySVG,
  equalizerSVG,
  databaseLargeSVG,
  chatSVG,
  shopSVG,
  tickWithCircleSVG,
  historySVG,
  agentSVG,
  scheduleSVG,
  twoUsersSVG,
} from '../allCustomSVGs';
import { allRoutes } from './route';

// Define the SidebarLink type
type SidebarLink = {
  icon: string | JSX.Element;
  iconType: string;
  title: string;
  link: string;
  urlName: string;
  level: number;
  specialURL?: string;
};

// Define the type for all routes keys
type RouteKey = keyof typeof allRoutes;

export const linksDashboard: SidebarLink[] = [
  {
    icon: dashboardSVG,
    iconType: 'svg',
    title: 'StatsDashboard',
    link: '/dashboard',
    urlName: 'dashboard',
    level: 1,
  },
];

// Phase 13 — top-level "Stats" entry. The page was previously deep-linked
// at `/agency/stats` only (no nav). Both paths render the same component
// (see App.tsx). Role-gated to owner/team_lead via the sidebar's
// `linkCapabilityGuards` map.
export const linksStats: SidebarLink[] = [
  {
    icon: equalizerSVG,
    iconType: 'svg',
    title: 'Stats',
    link: '/stats',
    urlName: 'stats',
    level: 1,
  },
];

// W5 — top-level "Audit log" entry. Page lives at `/audit-log` (see App.tsx).
// Role-gated to owner/team_lead in `SidebarTara` (mirrors the Stats link gate);
// the page itself also gates on the `audit.view` capability.
export const linksAuditLog: SidebarLink[] = [
  {
    icon: historySVG,
    iconType: 'svg',
    title: 'AuditLog',
    link: '/audit-log',
    urlName: 'audit-log',
    level: 1,
  },
];

// Per-agency "Meta API activity" entry. Page lives at `/meta-activity` (see
// App.tsx). Sits right after Audit log. Role-gated to owner/team_lead in
// `SidebarTara`; the page itself also gates on the `meta_activity.view`
// capability.
export const linksMetaActivity: SidebarLink[] = [
  {
    icon: databaseLargeSVG,
    iconType: 'svg',
    title: 'MetaActivity',
    link: '/meta-activity',
    urlName: 'meta-activity',
    level: 1,
  },
];

// Unified "Log Center" entry — replaces the separate Audit log + Meta API
// activity links in the sidebar. The page (`/logs`) hosts both as tabs; the
// old routes redirect into it. Role-gated to owner/team_lead in `SidebarTara`;
// the page shows whichever tabs the user's capabilities allow.
export const linksLogCenter: SidebarLink[] = [
  {
    icon: historySVG,
    iconType: 'svg',
    title: 'LogCenter',
    link: '/logs',
    urlName: 'logs',
    level: 1,
  },
];

export const linksClients: SidebarLink[] = [
  {
    icon: shopSVG,
    iconType: 'svg',
    title: 'Clients',
    link: '/clients',
    urlName: 'clients',
    level: 1,
  },
];

// The single "Integrations" entry for the unified hub at `/integrations`
// (platform connections + MCP servers/governance + tool access as tabs; the
// old Control Center and Tool Access entries are gone — `/control-center`
// redirects into the hub). Role-gated to owner/team_lead/manager via the
// sidebar's `linkCapabilityGuards` map (`integrations.view` — all roles; the
// page gates its own mutations on `integrations.manage`).
export const linksIntegrations: SidebarLink[] = [
  {
    icon: databaseLargeSVG,
    iconType: 'svg',
    title: 'Integrations',
    link: '/integrations',
    urlName: 'integrations',
    level: 1,
  },
];

// Phase 13 — nested "Brands" parent that groups the brand switcher (/clients)
// with the platform integrations management. Rendered via the same nested
// section path as the Reports/Actions menus in `SidebarTara`.
export const linksBrands: SidebarLink[] = [
  {
    icon: shopSVG,
    iconType: 'svg',
    title: 'AllBrands',
    link: '/clients',
    urlName: 'clients',
    level: 2,
  },
  // The Integrations sub-link was removed — the standalone Integrations page is
  // gone; its platform grid + MCP flows now live in the Control Center tabs.
];

// Phase 13 — Team is a top-level main-menu entry, mounted at `/team`. The
// page lives OUTSIDE `/settings/*` so the sidebar's startsWith('/settings')
// check stays in main-menu mode while the user is here. The legacy
// `/settings/team` URL still resolves (it redirects to /team) so external
// deep-links keep working. The sidebar uses the `team.view` capability to
// gate rendering for analyst/read_only roles.
export const linksTeam: SidebarLink[] = [
  {
    icon: twoUsersSVG,
    iconType: 'svg',
    title: 'Team',
    link: '/team',
    urlName: 'team',
    level: 1,
  },
];

// Phase 13 — per-employee Claude Code plugin setup page. Shows the user's
// API key, a ready-to-paste `.mcp.json` snippet, and a connectivity test.
export const linksMcpSetup: SidebarLink[] = [
  {
    icon: keySVG,
    iconType: 'svg',
    title: 'MyMcpSetup',
    link: '/my-mcp-setup',
    urlName: 'my-mcp-setup',
    level: 1,
  },
];

// Reporting portal (agency review/edit/approve surface). A SINGLE top-level
// entry landing on the Reporting home at `/client-reports` (report drafts are
// reviewed/approved via the Action Queue; the standalone review queue was
// retired). The former sub-nav
// entries (targets / branding / settings / domains) are now in-page tabs
// (see `ClientReportsNav`), not sidebar links. Role-gated to
// `portal.reports.review` via the sidebar's `linkCapabilityGuards` map; the
// page itself re-gates access. (URL path kept as `/client-reports`; only the
// display label was renamed to "Reporting".)
export const linksClientReports: SidebarLink[] = [
  {
    icon: equalizerSVG,
    iconType: 'svg',
    title: 'ClientReports',
    link: '/client-reports',
    urlName: 'client-reports',
    level: 1,
  },
];

// Agents hub (was the Skills hub at /skills; that path now redirects here).
export const linksAgents: SidebarLink[] = [
  {
    icon: agentSVG,
    iconType: 'svg',
    title: 'Agents',
    link: '/agents',
    urlName: 'agents',
    level: 1,
  },
];

export const linksPromptSchedules: SidebarLink[] = [
  {
    icon: scheduleSVG,
    iconType: 'svg',
    title: 'PromptSchedulesMenu',
    link: '/prompt-schedules',
    urlName: 'prompt-schedules',
    level: 1,
  },
];

export const linksReports: SidebarLink[] = [
  {
    icon: equalizerSVG,
    iconType: 'svg',
    title: 'Notifications',
    link: '/reports/notifications',
    urlName: 'notifications',
    specialURL: '/reports/notifications',
    level: 2,
  },
  {
    icon: equalizerSVG,
    iconType: 'svg',
    title: 'Presentations',
    link: '/reports/presentations',
    urlName: 'presentations',
    specialURL: '/reports/presentations',
    level: 2,
  },
  {
    icon: equalizerSVG,
    iconType: 'svg',
    title: 'Custom Prompts',
    link: '/reports/prompts',
    urlName: 'prompts',
    specialURL: '/reports/prompts',
    level: 2,
  },
  {
    icon: equalizerSVG,
    iconType: 'svg',
    title: 'Graphs',
    link: '/reports/graphs',
    urlName: 'graphs',
    specialURL: '/reports/graphs',
    level: 2,
  },
  // {
  //   icon: settingSVG,
  //   iconType: 'svg',
  //   title: 'Settings',
  //   link: '/insights/settings',
  //   urlName: 'settings',
  //   specialURL: '/insights/settings',
  //   level: 2,
  // },
];

export const linksAIOptimizations: SidebarLink[] = [
  {
    icon: equalizerSVG,
    iconType: 'svg',
    title: 'Automated',
    link: '/ai-optimizations/automated',
    urlName: 'automated',
    specialURL: '/ai-optimizations/automated',
    level: 2,
  },
  {
    icon: settingSVG,
    iconType: 'svg',
    title: 'Manual',
    link: '/ai-optimizations/manual',
    urlName: 'manual',
    specialURL: '/ai-optimizations/manual',
    level: 2,
  },
];

// DEPRECATED for nav: the 4-item nested "Actions" menu was replaced by the
// single `linksActionCenter` entry (below) plus the in-page `<ActionCenterTabs/>`
// strip. Kept only as documentation of the `/actions/*` sub-routes; not wired
// into any sidebar any more.
export const linksActions: SidebarLink[] = [
  {
    icon: tickWithCircleSVG,
    iconType: 'svg',
    title: 'Action Queue',
    link: '/actions/queue',
    urlName: 'queue',
    specialURL: '/actions/queue',
    level: 2,
  },
  {
    icon: historySVG,
    iconType: 'svg',
    title: 'Action History',
    link: '/actions/history',
    urlName: 'history',
    specialURL: '/actions/history',
    level: 2,
  },
  {
    icon: equalizerSVG,
    iconType: 'svg',
    title: 'Auto-Approval Rules',
    link: '/actions/rules',
    urlName: 'rules',
    specialURL: '/actions/rules',
    level: 2,
  },
];

// Action Center — single top-level sidebar entry that replaces the old 4-item
// nested "Actions" menu. It lands on the queue; the History / Auto-Approval
// Rules / Approval Settings surfaces are now tabs on the Action Center page
// (see `pages/actions/ActionsPage.tsx`). `urlName: 'actions'` keeps the entry
// highlighted across every `/actions/*` sub-route.
export const linksActionCenter: SidebarLink[] = [
  {
    icon: tickWithCircleSVG,
    iconType: 'svg',
    title: 'ActionCenter',
    link: '/actions/queue',
    urlName: 'actions',
    level: 1,
  },
];

export const linksRecommendations: SidebarLink[] = [
  {
    icon: RecommendationsSVG,
    iconType: 'svg',
    title: 'Recommendations',
    link: '/recommendations',
    urlName: 'recommendations',
    level: 1,
  },
];

export const linksTracking: SidebarLink[] = [
  {
    icon: rocketSVG,
    iconType: 'svg',
    title: 'Integration',
    link: '/tracking/integration',
    urlName: 'integration',
    level: 2,
  },
  {
    icon: classesSVG,
    iconType: 'svg',
    title: 'CSSTracking',
    link: '/tracking/css',
    urlName: 'css',
    level: 2,
  },
  {
    icon: eventSVG,
    iconType: 'svg',
    title: 'EventTracking',
    link: '/tracking/events',
    urlName: 'events',
    level: 2,
  },
  // {
  //   icon: autofillSVG,
  //   iconType: 'svg',
  //   title: 'Autofill',
  //   link: '/tracking/autofill',
  //   urlName: 'autofill',
  //   level: 2,
  // },
];

export const linksSettings: SidebarLink[] = [
  {
    icon: settingSVG,
    iconType: 'svg',
    title: 'General',
    link: '/settings/general',
    urlName: 'general',
    level: 2,
  },
  {
    icon: userSettingSVG,
    iconType: 'svg',
    title: 'Profile',
    link: '/settings/profile',
    urlName: 'profile',
    level: 2,
  },
  // Phase 13 — Team / Brands / Tara were relocated out of the Settings
  // sub-nav. Team is now a top-level main-menu entry (see `linksTeam`),
  // Brands lives under the nested "Brands" parent (`linksBrands`), and the
  // Tara configuration sub-page is hidden from nav (still reachable via
  // direct URL at /settings/tara for legacy deep-links).
  {
    icon: chatSVG,
    iconType: 'svg',
    title: 'Communication',
    link: '/settings/communication',
    urlName: 'communication',
    level: 2,
  },
  {
    icon: subscribeSVG,
    iconType: 'svg',
    title: 'Subscription',
    link: '/settings/subscription',
    urlName: 'subscription',
    level: 2,
  },
  {
    icon: keySVG,
    iconType: 'svg',
    title: 'API Keys',
    link: '/settings/api-keys',
    urlName: 'api-keys',
    level: 2,
  },
  {
    icon: designSVG,
    iconType: 'svg',
    title: 'Appearance',
    link: '/settings/appearance',
    urlName: 'appearance',
    level: 2,
  },
  {
    icon: pspSvg,
    iconType: 'svg',
    title: 'Payments',
    link: '/settings/payments',
    urlName: 'payments',
    level: 2,
  },
  {
    icon: shipSVG,
    iconType: 'svg',
    title: 'Shipping',
    link: '/settings/shipping',
    urlName: 'shipping',
    level: 2,
  },
  {
    icon: pspSvg,
    iconType: 'svg',
    title: 'ConvenienceFee',
    link: '/settings/convenienceFee',
    urlName: 'convenienceFee',
    level: 2,
  },
  {
    icon: discountsSVG,
    iconType: 'svg',
    title: 'Discounts',
    link: '/settings/discounts',
    urlName: 'discounts',
    level: 2,
  },
  {
    icon: notificationSVG,
    iconType: 'svg',
    title: 'Notifications',
    link: '/settings/notifications',
    urlName: 'notifications',
    level: 2,
  },
  {
    icon: webhookSVG,
    iconType: 'svg',
    title: 'Webhooks',
    link: '/settings/webhooks',
    urlName: 'webhooks',
    level: 2,
  },
  {
    icon: marketingSVG,
    iconType: 'svg',
    title: 'Marketing',
    link: '/settings/marketing',
    urlName: 'marketing',
    level: 2,
  },
  {
    icon: securitySVG,
    iconType: 'svg',
    title: 'Security',
    link: '/settings/security',
    urlName: 'security',
    level: 2,
  },
  // Phase 1 (custom roles) — owner-only "Roles" management page. The sidebar
  // gates this link on the `settings.manage_org` capability via
  // `linkCapabilityGuards` in SidebarTara (so only owners see it), and the
  // route itself is env-flagged in route.ts.
  {
    icon: securitySVG,
    iconType: 'svg',
    title: 'Roles',
    link: '/settings/roles',
    urlName: 'roles',
    level: 2,
  },
];

// Phase 13 — main menu ordering. `linksPromptSchedules` and
// `linksAIOptimizations` are intentionally OFF the main spread (pages remain
// reachable by direct URL — see `route.ts`). `linksReports` is off too, and its
// `/reports/*` routes are switched off in `route.ts`. The nested `linksBrands`
// parent replaces the flat `linksClients`/`linksIntegrations` entries.
export const sellerAllSidebarLinks: SidebarLink[] = [
  ...linksDashboard,
  ...linksStats,
  ...linksLogCenter,
  ...linksBrands,
  // Single canonical entry to the unified `/integrations` hub (platforms,
  // MCP servers/governance and tool access live there as tabs).
  ...linksIntegrations,
  // Single "Action Center" entry (was the 4-item `linksActions` nested menu).
  ...linksActionCenter,
  ...linksTeam,
  ...linksAgents,
  ...linksRecommendations,
  ...linksMcpSetup,
  ...linksTracking,
  ...linksSettings,
].filter((link) => allRoutes[link.link as RouteKey]);
